import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';

import { showErrorToUser, useConfirm } from '@pletly/ui';
import { useAuth, useLoadingOverlay } from '%context';
import { useUser } from '%hooks';

import { GetProtegesDocument } from '%context/Connections/__generated__/getProteges.generated';
import { UserBasicInfoFragment } from '%fragments/__generated__/userBasicInfo.generated';
import { useDeleteAccountMutation } from './__generated__/deleteAccount.mutation.generated';

export const useDeleteAccount = <UserInfo extends Pick<UserBasicInfoFragment, 'id' | 'username'>>(
	userInfo: UserInfo,
) => {
	const { t } = useTranslation();

	const [deleteAccount] = useDeleteAccountMutation();
	const { confirm } = useConfirm();
	const { logout: signOut } = useAuth();
	const { navigate } = useNavigation();

	const { id: loggedUserId } = useUser();

	const isCurrentUser = loggedUserId === userInfo.id;

	const { showLoadingOverlay, closeLoadingOverlay } = useLoadingOverlay();

	const handleDeleteSuccess = useMemo(
		() => (isCurrentUser ? signOut : () => navigate('Connections')),
		[isCurrentUser, signOut, navigate],
	);

	const handleDeleteAccount = useCallback(async () => {
		const confirmation = await confirm(
			t('profile.delete-profile-username-account', { username: userInfo.username }),
			t('profile.are-you-sure-you-want-to-delete-profile-username-account-forever', {
				username: userInfo.username,
			}),
		);
		if (!confirmation) return;

		showLoadingOverlay();

		try {
			await deleteAccount({
				variables: { userId: String(userInfo.id) },
				refetchQueries: isCurrentUser ? [] : [GetProtegesDocument],
			});

			return handleDeleteSuccess();
		} catch (error) {
			console.error(error);
			if (error instanceof Error) {
				showErrorToUser(error);
			}
		} finally {
			closeLoadingOverlay();
		}
	}, [
		confirm,
		t,
		userInfo.username,
		userInfo.id,
		showLoadingOverlay,
		deleteAccount,
		isCurrentUser,
		handleDeleteSuccess,
		closeLoadingOverlay,
	]);

	return { handleDeleteAccount };
};
